/**
 * Utility functions for the List Campaigns API.
 *
 * Transformation and formatting logic specific to the
 * value-matching list campaigns operation.
 */

import { CampaignData, CampaignTableRow } from 'src/@types/valueMatching/campaign';
import { CampaignType } from 'src/@types/valueMatching/enums';
import { API_ENDPOINTS } from 'src/routes/valueMatching/routes';
import { isValidCampaignType } from 'src/utils/valueMatching/commonUtils';
import { formatEpochToIST } from 'src/utils/commonUtils';

export { isValidCampaignType };
export { formatEpochToIST } from 'src/utils/commonUtils';
export { clearDateCache } from 'src/utils/commonUtils';

// ============================================================================
// URL building
// ============================================================================

/**
 * Builds a properly encoded API URL for the list campaigns endpoint.
 *
 * @param campaignType - The validated campaign type to append as a path segment
 * @returns Fully constructed URL string
 */
export function buildListCampaignsUrl(campaignType: CampaignType): string {
  const baseUrl = API_ENDPOINTS.VALUE_MATCHING_LIST_CAMPAIGNS;
  return `${baseUrl}/${encodeURIComponent(campaignType)}`;
}

// ============================================================================
// Row transformation
// ============================================================================

const CPGN_NOT_FOUND = 'CPGN Account Not Found';

/**
 * Transforms a single CampaignData (API shape) into a CampaignTableRow (UI display shape).
 * Mirrors the old Java CampaignListBuilder.build() logic.
 *
 * @param overview - Raw campaign data from API response
 * @returns Flattened, pre-formatted row ready for table rendering
 */
export function toCampaignTableRow(overview: CampaignData): CampaignTableRow {
  return {
    id: overview.campaignId ?? '',
    title: overview.title ?? '',
    cpgn: overview.cpgnAccount ?? '',
    fundingRemaining:
      overview.funding?.currencyValue !== undefined && overview.funding?.currencyValue !== null
        ? String(overview.funding.currencyValue)
        : CPGN_NOT_FOUND,
    merchant: overview.merchantDetail?.merchantGroup ?? null,
    startTime: formatEpochToIST(overview.startTime),
    endTime: formatEpochToIST(overview.endTime),
    createdBy: overview.createdBy ?? '',
    lastModified: formatEpochToIST(overview.lastModifiedTime),
    lastModifiedBy: overview.lastModifiedBy ?? '',
    status: overview.campaignStatus ?? '',
    version: overview.version !== undefined ? String(overview.version) : '',
  };
}

/**
 * Transforms a list of CampaignData objects into CampaignTableRow array.
 *
 * @param overviews - Array of raw campaign data from API
 * @returns Array of display-ready table rows
 */
export function toCampaignTableRows(overviews: CampaignData[]): CampaignTableRow[] {
  return overviews.map(toCampaignTableRow);
}
